/**
 * DeviceGuard — gates the FIELD-ONLY kiosk + face-enrolment screens to phones &
 * tablets. On a desktop/laptop it replaces the ENTIRE page with a block screen
 * (no camera ever initialises) and logs the blocked attempt for admins.
 *
 * Primary gate = client touch/pointer detection. The backend kiosk_identify UA
 * check is the secondary layer for direct API bypass.
 */
import { useEffect, useRef, useState } from 'react';
import { Smartphone } from 'lucide-react';
import { isMobileOrTablet, deviceCategory } from '../lib/deviceDetect';
import { apiPost } from '../lib/api';

export function DesktopBlockScreen() {
  return (
    <div
      data-testid="device-block-screen"
      className="min-h-screen bg-[#0f1a2e] text-white flex flex-col items-center justify-center p-8 text-center"
    >
      <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
        <Smartphone size={32} className="text-white/80" />
      </div>
      <h1 className="font-display font-bold text-2xl md:text-3xl mb-3">Mobile &amp; tablet only</h1>
      <p className="text-white/60 max-w-md text-sm md:text-base leading-relaxed">
        The Attendance Kiosk is only available on mobile phones and tablets.
        Please open this page on your site device.
      </p>
    </div>
  );
}

/**
 * Returns: null while checking, true if allowed (mobile/tablet), false if blocked.
 * Logs a blocked desktop attempt to the backend exactly once.
 */
export function useDeviceGuard(context = 'kiosk') {
  const [allowed, setAllowed] = useState(null);
  const logged = useRef(false);
  useEffect(() => {
    const ok = isMobileOrTablet();
    setAllowed(ok);
    if (!ok && !logged.current) {
      logged.current = true;
      apiPost('/mobile/attendance/kiosk/device-block-log', {
        context,
        user_agent: navigator.userAgent,
        device_category: deviceCategory(),
        screen_width: window.screen && window.screen.width,
        screen_height: window.screen && window.screen.height,
        inner_width: window.innerWidth,
        inner_height: window.innerHeight,
      }).catch(() => {});
    }
  }, [context]);
  return allowed;
}
